import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';
import { playNotificationSound } from '../../utils/notificationSound';

import API_BASE from '../../utils/api';
const API = API_BASE;

const channelOptions = [
  { value: 'in_app', label: 'In-App', icon: '🔔', desc: 'App notification only' },
  { value: 'sms', label: 'SMS', icon: '📱', desc: 'Text message to phone' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '💬', desc: 'WhatsApp message' },
  { value: 'all', label: 'All Channels', icon: '📢', desc: 'Send via all channels' },
];

const typeOptions = [
  { value: 'general', label: 'General' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'fee_reminder', label: 'Fee Reminder' },
  { value: 'exam', label: 'Exam' },
  { value: 'event', label: 'Event' },
  { value: 'leave', label: 'Leave' },
];

const Communications = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [sendMode, setSendMode] = useState('individual');
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [historyFilter, setHistoryFilter] = useState('all');

  const [form, setForm] = useState({
    classId: '', studentId: '', title: '', message: '',
    channel: 'whatsapp', type: 'general'
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes`, { headers });
      setClasses(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchStudents = useCallback(async (classId) => {
    if (!classId) { setStudents([]); return; }
    try {
      const res = await axios.get(`${API}/classes/${classId}/students`, { headers });
      setStudents(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const params = historyFilter !== 'all' ? `?channel=${historyFilter}` : '';
      const res = await axios.get(`${API}/communications/history${params}`, { headers });
      setHistory(res.data.messages || []);
      setHistoryTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [historyFilter]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { if (activeTab === 'history') fetchHistory(); }, [activeTab, fetchHistory]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleClassChange = (classId) => {
    setForm({ ...form, classId, studentId: '' });
    if (sendMode === 'individual') fetchStudents(classId);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message || !form.channel) {
      return showMsg('error', 'Please fill all required fields');
    }
    setLoading(true);
    try {
      let endpoint, payload;
      if (sendMode === 'individual') {
        if (!form.studentId) return showMsg('error', 'Please select a student');
        endpoint = `${API}/communications/send`;
        payload = { studentId: form.studentId, title: form.title, message: form.message, channel: form.channel, type: form.type };
      } else if (sendMode === 'class') {
        if (!form.classId) return showMsg('error', 'Please select a class');
        endpoint = `${API}/communications/send-class`;
        payload = { classId: form.classId, title: form.title, message: form.message, channel: form.channel, type: form.type };
      } else {
        endpoint = `${API}/communications/broadcast`;
        payload = { title: form.title, message: form.message, channel: form.channel, type: form.type };
      }

      const res = await axios.post(endpoint, payload, { headers });
      const info = res.data;
      if (sendMode === 'individual') {
        const statuses = (info.results || []).map(r => `${r.channel}: ${r.status}`).join(', ');
        showMsg('success', `Sent! (${statuses})`);
      } else {
        showMsg('success', info.message || 'Messages sent!');
      }
      playNotificationSound(); // Play sound on successful send
      setForm({ ...form, title: '', message: '' });
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to send');
    } finally { setLoading(false); }
  };

  const getChannelBadge = (ch) => {
    switch(ch) {
      case 'sms': return 'bg-blue-100 text-blue-700';
      case 'whatsapp': return 'bg-green-100 text-green-700';
      case 'in_app': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadge = (st) => {
    switch(st) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Communications</h1>
              <p className="text-gray-500 mt-1">Send SMS, WhatsApp & in-app notifications to parents</p>
            </div>
            <ProfileDropdown />
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            <button onClick={() => setActiveTab('send')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'send' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              Send Message
            </button>
            <button onClick={() => setActiveTab('history')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'history' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}>
              History ({historyTotal})
            </button>
          </div>

          {activeTab === 'send' && (
            <div className="max-w-3xl">
              <div className="flex gap-2 mb-6">
                {[{ v: 'individual', l: 'Individual Student', i: '👤' }, { v: 'class', l: 'Entire Class', i: '🏫' }, { v: 'broadcast', l: 'All Parents', i: '📢' }].map(m => (
                  <button key={m.v} onClick={() => { setSendMode(m.v); setForm({ ...form, classId: '', studentId: '' }); setStudents([]); }}
                    className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${sendMode === m.v ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <span className="text-2xl block">{m.i}</span>
                    <span className="text-sm font-medium">{m.l}</span>
                  </button>
                ))}
              </div>

              <form onSubmit={handleSend} className="bg-white rounded-xl shadow-lg p-6">
                {sendMode !== 'broadcast' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Class *</label>
                      <select value={form.classId} onChange={(e) => handleClassChange(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2" required>
                        <option value="">Choose class...</option>
                        {classes.map(c => <option key={c._id} value={c._id}>{c.className} - {c.section}</option>)}
                      </select>
                    </div>
                    {sendMode === 'individual' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Student *</label>
                        <select value={form.studentId} onChange={(e) => setForm({ ...form, studentId: e.target.value })}
                          className="w-full border rounded-lg px-3 py-2" required>
                          <option value="">Choose student...</option>
                          {students.map(s => <option key={s._id} value={s._id}>{s.rollNumber} - {s.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Channel *</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {channelOptions.map(ch => (
                      <button type="button" key={ch.value} onClick={() => setForm({ ...form, channel: ch.value })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${form.channel === ch.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className="text-xl block">{ch.icon}</span>
                        <span className="text-xs font-medium block">{ch.label}</span>
                        <span className="text-xs text-gray-400">{ch.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notification Type</label>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2">
                      {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" placeholder="e.g. Fee Reminder" required />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2" rows="4"
                    placeholder={sendMode === 'class' ? 'Use {studentName} to include student name in each message' : 'Type your message here...'}
                    required />
                  {sendMode === 'class' && (
                    <p className="text-xs text-gray-500 mt-1">Tip: Use <strong>{'{studentName}'}</strong> to personalize — e.g. "Dear parent of {'{studentName}'}"</p>
                  )}
                </div>

                {(form.channel === 'sms' || form.channel === 'whatsapp' || form.channel === 'all') && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                    Note: SMS and WhatsApp are currently in simulation mode. Configure your SMS/WhatsApp API provider in the backend to enable real delivery.
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50">
                  {loading ? 'Sending...' : `Send via ${channelOptions.find(c => c.value === form.channel)?.label || form.channel}`}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div className="flex gap-2 mb-4">
                {['all', 'in_app', 'sms', 'whatsapp'].map(ch => (
                  <button key={ch} onClick={() => setHistoryFilter(ch)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyFilter === ch ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
                    {ch === 'all' ? 'All' : ch === 'in_app' ? 'In-App' : ch === 'sms' ? 'SMS' : 'WhatsApp'}
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="p-3 text-xs font-semibold text-gray-500">Date</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">Channel</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">To (Parent)</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">Student</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">Title</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">Message</th>
                      <th className="p-3 text-xs font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr><td colSpan="7" className="p-8 text-center text-gray-400">No messages sent yet</td></tr>
                    ) : history.map(h => (
                      <tr key={h._id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-600">{new Date(h.createdAt).toLocaleString()}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelBadge(h.channel)}`}>
                            {h.channel === 'in_app' ? 'In-App' : h.channel === 'sms' ? 'SMS' : 'WhatsApp'}
                          </span>
                        </td>
                        <td className="p-3 text-sm">{h.userId?.name || '-'}<br/><span className="text-xs text-gray-400">{h.sentTo}</span></td>
                        <td className="p-3 text-sm">{h.studentId?.name || '-'}</td>
                        <td className="p-3 text-sm font-medium">{h.title}</td>
                        <td className="p-3 text-sm text-gray-600 max-w-xs truncate">{h.message}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(h.deliveryStatus)}`}>
                            {h.deliveryStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Communications;